import { useState, useEffect, useCallback } from 'react';
import { X, Download } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pwa-install-dismissed';
const SHOWN_TODAY_KEY = 'pwa-install-shown-date';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days
const AUTO_HIDE_MS = 8000; // 8 seconds (within 5-10s range)

function getTodayDateString(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [sliding, setSliding] = useState(false); // controls slide-out animation

  const hide = useCallback(() => {
    setSliding(true);
    // Let the slide-out animation finish before unmounting
    setTimeout(() => setVisible(false), 300);
  }, []);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    hide();
  }, [hide]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      hide();
    }
    setDeferredPrompt(null);
  }, [deferredPrompt, hide]);

  useEffect(() => {
    // 1. Check 7-day dismiss (user clicked "Not now" / X)
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed) < DISMISS_DURATION) {
      return;
    }

    // 2. Check if already shown today (once per day)
    const shownDate = localStorage.getItem(SHOWN_TODAY_KEY);
    const today = getTodayDateString();
    if (shownDate === today) {
      return;
    }

    // 3. Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Mark that we've shown the prompt today
      localStorage.setItem(SHOWN_TODAY_KEY, today);
      setSliding(false);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Auto-hide after 8 seconds
  useEffect(() => {
    if (!visible || sliding) return;
    const timer = setTimeout(() => {
      hide();
    }, AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [visible, sliding, hide]);

  if (!visible) return null;

  return (
    <div
      className={`fixed top-0 right-0 h-full z-50 pointer-events-none flex items-start justify-end pt-20 pr-4 transition-transform duration-300 ease-in-out ${
        sliding ? 'translate-x-full' : 'translate-x-0'
      }`}
      style={{ width: 'auto', maxWidth: '24rem' }}
    >
      <div className="pointer-events-auto bg-card text-card-foreground rounded-xl shadow-lift border border-border p-4 flex items-start gap-3 w-80">
        <div className="flex-shrink-0 w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
          <Download className="w-5 h-5 text-accent-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Install CustodyPlanner</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Add to your home screen for quick access and offline support.
          </p>
          <div className="flex gap-2 mt-2.5">
            <button
              onClick={handleInstall}
              className="px-3 py-1.5 text-xs font-semibold text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 transition-colors"
            >
              Install
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-muted-foreground/70 hover:text-foreground transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
